import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Choice, Note } from "@/components/automations/primitives"
import { RoutePending } from "@/components/app/states"
import { Metrics } from "@/components/automations/primitives"
import { toMetrics } from "@/components/reports/helpers"
import { DefinitionsCard, ReportHeader } from "@/components/reports/primitives"
import { formatDateTime } from "@/lib/format"
import { campaignReportOptionsQuery, campaignReportQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/reports/campaigns")({
  validateSearch: z.object({ campaignId: z.string().optional() }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(campaignReportOptionsQuery(params.workspace)),
  component: CampaignReportPage,
})

const SHADES = ["bg-foreground", "bg-foreground/40", "bg-foreground/25", "bg-foreground/10", "bg-foreground/60", "bg-foreground/15"]

/** Screen 38. */
function CampaignReportPage() {
  const { workspace } = useWorkspaceContext()
  const options = useSuspenseQuery(campaignReportOptionsQuery(workspace.slug)).data
  const { campaignId = options[0]?.id } = Route.useSearch()
  const navigate = Route.useNavigate()
  const report = useQuery({ ...campaignReportQuery(workspace.slug, campaignId ?? ""), enabled: Boolean(campaignId), placeholderData: (p) => p })
  const data = report.data
  const params = { workspace: workspace.slug }
  return (
    <div className="page-wrap auto-page">
      <ReportHeader title="Campaign performance" description={data ? `${data.name} · ${data.provider === "messenger" ? "Messenger" : "Instagram"}${data.sentAt ? ` · sent ${formatDateTime(data.sentAt)}` : ""}` : "One campaign, one channel, explicit signals."} workspace={workspace.slug} exportPath={`/api/w/${workspace.slug}/reports/campaigns/${campaignId ?? ""}.csv`} exportName="campaign">
        {options.length > 0 && <Choice label="Campaign" value={campaignId ?? ""} onChange={(v) => void navigate({ search: { campaignId: v } })} values={options.map((o) => ({ value: o.id, label: `${o.name} · ${o.status}` }))} />}
      </ReportHeader>
      {!options.length ? (
        <Card>
          <CardHeader>
            <CardTitle>No approved campaigns yet</CardTitle>
            <CardDescription>Reports appear once a campaign has been sent or scheduled.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to="/w/$workspace/campaigns" params={params}>
                Go to campaigns
                <ArrowRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : !data ? (
        <RoutePending />
      ) : (
        <>
          <Metrics items={toMetrics(data.metrics)} />
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Delivery outcomes</CardTitle>
                <CardDescription>{data.eligible.toLocaleString()} eligible recipients · latest known state</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-7 flex h-7 overflow-hidden rounded-md bg-muted" role="img" aria-label={data.outcomes.map((o) => `${o.value} ${o.label.toLowerCase()}`).join(", ")}>
                  {data.outcomes.map((o, i) => (
                    <span key={o.key} className={SHADES[i % SHADES.length]} style={{ width: `${o.share}%` }} />
                  ))}
                </div>
                <div className="divide-y">
                  {data.outcomes.map((o, i) => (
                    <Link className="flex w-full items-center gap-3 py-3 text-left text-sm" key={o.key} to="/w/$workspace/campaigns/$campaignId/recipients" params={{ ...params, campaignId: data.campaignId }} search={{ outcome: o.key === "failed" ? "failed" : o.key }}>
                      <span className="size-2 rounded-full bg-foreground" style={{ opacity: 1 - i * 0.18 }} />
                      <span className="flex-1">{o.label}</span>
                      <span className="w-12 text-right font-medium tabular-nums">{o.value}</span>
                      <span className="w-14 text-right text-muted-foreground tabular-nums">{o.share}%</span>
                      <ArrowUpRight className="size-3 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Replies</CardTitle>
                <CardDescription>Conversations started by this campaign.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="my-5 text-5xl font-medium tracking-tight">
                  {data.replies.rate === null ? "—" : data.replies.rate}
                  <span className="text-3xl text-muted-foreground">{data.replies.rate === null ? "" : "%"}</span>
                </p>
                <p className="text-sm">
                  {data.replies.unique} unique replies / {data.replies.delivered} delivered
                </p>
                <p className="mt-4 text-xs leading-5 text-muted-foreground">Replies are a subset of delivered recipients, measured within this campaign’s reporting window.</p>
                <Button variant="outline" className="mt-6 w-full" asChild>
                  <Link to="/w/$workspace/campaigns/$campaignId/recipients" params={{ ...params, campaignId: data.campaignId }} search={{}}>
                    View recipients
                    <ArrowRight />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Audience and send checks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-5 sm:grid-cols-3">
                  {[
                    ["Selected audience", data.checks.selected, "Before exclusions"],
                    ["Initially excluded", data.checks.excluded, "Permission, window or channel"],
                    ["Skipped at send", data.checks.skipped, "No longer eligible"],
                  ].map(([label, value, detail]) => (
                    <div key={String(label)}>
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="my-3 text-2xl font-medium tabular-nums">{Number(value).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{detail}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/40">
              <CardHeader>
                <CardTitle>Available signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.signals.map((s) => (
                  <div className="flex items-center justify-between gap-2 text-sm" key={s.label}>
                    <span>{s.label}</span>
                    <span className={s.value === null ? "text-muted-foreground" : "font-medium"}>{s.value ?? "Unavailable"}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="mt-6">
            <DefinitionsCard title="Definitions in this report" definitions={data.definitions} />
          </div>
          <Note>Each campaign uses one channel. Accepted = delivered + awaiting receipts. Unavailable read and click signals are not zero.</Note>
        </>
      )}
    </div>
  )
}
